import React, { useMemo } from "react";
import { Check, X } from "lucide-react";

// Returns { score: 0..4, label, color, checks: [{key, label, ok}], suggestions: [string] }
export const scorePassword = (pw = "") => {
  const len = pw.length;
  const has = {
    length:    len >= 8,
    lengthTwelve: len >= 12,
    lower:     /[a-z]/.test(pw),
    upper:     /[A-Z]/.test(pw),
    digit:     /\d/.test(pw),
    symbol:    /[^A-Za-z0-9]/.test(pw),
  };

  // Reject obvious bad passwords outright.
  const COMMON = new Set([
    "password", "password1", "password123", "12345678", "123456789",
    "qwerty", "qwerty123", "letmein", "welcome", "admin", "iloveyou",
    "abc12345", "monkey", "dragon", "sunshine", "princess",
  ]);
  const isCommon = COMMON.has(pw.toLowerCase());

  // Start at 0 — must earn each point.
  let score = 0;
  if (has.length)   score += 1;
  if (has.lower && has.upper) score += 1;
  if (has.digit)    score += 1;
  if (has.symbol)   score += 1;
  if (has.lengthTwelve && score >= 3) score = 4; // bump to "Strong" when long + varied
  if (!has.length || isCommon) score = Math.min(score, 1);
  if (isCommon)     score = 0;

  const LABELS = ["Too weak", "Weak", "Okay", "Good", "Strong"];
  const COLORS = ["#EF4444", "#F97316", "#F59E0B", "#22C55E", "#10B981"];

  const checks = [
    { key: "length", label: "At least 8 characters", ok: has.length },
    { key: "case",   label: "Upper & lower case",    ok: has.lower && has.upper },
    { key: "digit",  label: "Contains a number",     ok: has.digit },
    { key: "symbol", label: "Contains a symbol (!@#…)", ok: has.symbol },
  ];

  const suggestions = [];
  if (isCommon) suggestions.push("That password is too common. Pick something unique.");
  if (!has.length) suggestions.push("Use at least 8 characters.");
  else if (!has.lengthTwelve && score < 4) suggestions.push("12+ characters makes it Strong.");
  if (!(has.lower && has.upper)) suggestions.push("Mix upper and lower case letters.");
  if (!has.digit) suggestions.push("Add a number.");
  if (!has.symbol) suggestions.push("Add a symbol like ! @ # $.");

  return {
    score,
    label: LABELS[score],
    color: COLORS[score],
    checks,
    suggestions,
    isStrong: score >= 3,
  };
};

const PasswordStrengthMeter = ({ password = "", showChecks = true, className = "" }) => {
  const { score, label, color, checks, suggestions } = useMemo(() => scorePassword(password), [password]);

  // Hide entirely until the user types something — keeps forms clean on first render.
  if (!password) return null;

  return (
    <div className={`mt-2 ${className}`} data-testid="pw-strength-meter">
      {/* Segmented bar */}
      <div className="flex items-center gap-1.5" aria-label={`Password strength: ${label}`}>
        {[0, 1, 2, 3].map((i) => {
          const active = i < score;
          return (
            <div
              key={i}
              data-testid={`pw-strength-seg-${i}`}
              className="h-1.5 flex-1 rounded-full transition-all duration-300"
              style={{
                background: active ? color : "#E2E8F0",
                boxShadow: active ? `0 0 8px ${color}55` : "none",
              }}
            />
          );
        })}
        <span
          data-testid="pw-strength-label"
          className="text-[11px] font-bold uppercase tracking-wider ml-2 min-w-[58px] text-right transition-colors"
          style={{ color }}
        >
          {label}
        </span>
      </div>

      {/* Per-rule checklist */}
      {showChecks && (
        <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5" data-testid="pw-strength-checks">
          {checks.map((c) => (
            <li
              key={c.key}
              className={`flex items-center gap-1.5 text-[11px] transition-colors ${
                c.ok ? "text-emerald-600" : "text-slate-400"
              }`}
              data-testid={`pw-check-${c.key}`}
            >
              {c.ok ? (
                <Check className="w-3 h-3 shrink-0" />
              ) : (
                <X className="w-3 h-3 shrink-0" />
              )}
              <span className={c.ok ? "line-through decoration-emerald-300" : ""}>{c.label}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Top suggestion (only show the first one to avoid noise) */}
      {suggestions.length > 0 && score < 4 && (
        <p className="mt-2 text-[11px] text-slate-500" data-testid="pw-strength-tip">
          <span className="font-semibold text-slate-700">Tip:</span> {suggestions[0]}
        </p>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
